/**
 * CrewImportPage - Safe, controlled CSV import for crew records
 * PROMPT 43: Crew CSV Import
 */
import { useState, useCallback } from "react";
import {
  previewStaffImport,
  executeStaffImport,
  getStaffImportLogs,
  extractErrorMessage,
} from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  SkipForward,
  Plus,
  RefreshCw,
  Loader2,
  History,
  AlertTriangle,
  Info,
} from "lucide-react";
import { format } from "date-fns";

const IMPORT_MODES = {
  create_new: {
    label: "Create New Crew",
    description: "Only create new crew records. Skip rows where email already exists.",
  },
  update_existing: {
    label: "Update Existing Crew",
    description: "Only update existing crew by email. Skip rows where email is not found.",
  },
};

/**
 * Status badge for import row
 */
function RowStatusBadge({ status }) {
  switch (status) {
    case "create":
      return (
        <Badge className="bg-green-900/50 text-green-300 border-green-700">
          <Plus className="h-3 w-3 mr-1" />
          Create
        </Badge>
      );
    case "update":
      return (
        <Badge className="bg-blue-900/50 text-blue-300 border-blue-700">
          <RefreshCw className="h-3 w-3 mr-1" />
          Update
        </Badge>
      );
    case "skip":
      return (
        <Badge className="bg-yellow-900/50 text-yellow-300 border-yellow-700">
          <SkipForward className="h-3 w-3 mr-1" />
          Skip
        </Badge>
      );
    case "error":
      return (
        <Badge className="bg-red-900/50 text-red-300 border-red-700">
          <XCircle className="h-3 w-3 mr-1" />
          Error
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

/**
 * File drop zone component
 */
function FileDropZone({ onFileSelect, disabled }) {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files[0]) {
        onFileSelect(files[0]);
      }
    },
    [onFileSelect, disabled]
  );

  const handleChange = useCallback(
    (e) => {
      const files = e.target.files;
      if (files && files[0]) {
        onFileSelect(files[0]);
      }
    },
    [onFileSelect]
  );

  return (
    <div
      className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        dragActive
          ? "border-teal-500 bg-teal-500/10"
          : "border-border hover:border-border"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      data-testid="file-dropzone"
    >
      <input
        type="file"
        accept=".csv"
        onChange={handleChange}
        disabled={disabled}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
      <p className="text-sm text-foreground mb-1">
        Drag and drop a CSV file here, or click to browse
      </p>
      <p className="text-xs text-muted-foreground">
        Supported columns: first_name, last_name, email, phone, role, qualifications, status
      </p>
    </div>
  );
}

/**
 * Preview table component
 */
function PreviewTable({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">No rows to preview</p>
    );
  }

  return (
    <div className="max-h-[400px] overflow-auto border border-border rounded-lg">
      <Table>
        <TableHeader className="sticky top-0 bg-surface-elevated">
          <TableRow>
            <TableHead className="w-[60px]">Row</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Issues</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.row_number}
              className={
                row.status === "error" ? "bg-red-900/20" : ""
              }
              data-testid={`preview-row-${row.row_number}`}
            >
              <TableCell className="font-mono text-xs">{row.row_number}</TableCell>
              <TableCell>
                <RowStatusBadge status={row.status} />
              </TableCell>
              <TableCell>
                {row.first_name} {row.last_name}
              </TableCell>
              <TableCell className="text-xs">{row.email}</TableCell>
              <TableCell className="text-xs">{row.phone || "-"}</TableCell>
              <TableCell className="text-xs">{row.role || "-"}</TableCell>
              <TableCell>
                {row.errors?.length > 0 && (
                  <span className="text-xs text-red-400">
                    {row.errors.join(", ")}
                  </span>
                )}
                {row.warnings?.length > 0 && (
                  <span className="text-xs text-yellow-400">
                    {row.warnings.join(", ")}
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Import logs table
 */
function ImportLogsTable({ logs, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">No import history</p>
    );
  }

  return (
    <div className="max-h-[300px] overflow-auto border border-border rounded-lg">
      <Table>
        <TableHeader className="sticky top-0 bg-surface-elevated">
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Mode</TableHead>
            <TableHead className="text-center">Total</TableHead>
            <TableHead className="text-center">Created</TableHead>
            <TableHead className="text-center">Updated</TableHead>
            <TableHead className="text-center">Skipped</TableHead>
            <TableHead className="text-center">Errors</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <TableRow key={log.id}>
              <TableCell className="text-xs">
                {log.imported_at
                  ? format(new Date(log.imported_at), "dd MMM yyyy HH:mm")
                  : "-"}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {log.import_mode === "create_new" ? "Create" : "Update"}
                </Badge>
              </TableCell>
              <TableCell className="text-center">{log.total_rows}</TableCell>
              <TableCell className="text-center text-green-400">{log.created_count}</TableCell>
              <TableCell className="text-center text-blue-400">{log.updated_count}</TableCell>
              <TableCell className="text-center text-yellow-400">{log.skipped_count}</TableCell>
              <TableCell className="text-center text-red-400">{log.error_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Main Crew Import Page
 */
export default function StaffImportPage() {
  const [step, setStep] = useState("upload"); // upload, preview, results
  const [importMode, setImportMode] = useState("create_new");
  const [csvContent, setCsvContent] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  // Import logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const handleFileSelect = async (file) => {
    if (!file.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }

    setFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      setCsvContent(content);
      await handlePreview(content);
    };
    reader.onerror = () => {
      toast.error("Failed to read file");
    };
    reader.readAsText(file);
  };

  const handlePreview = async (content) => {
    setLoading(true);
    setPreview(null);
    
    try {
      const res = await previewStaffImport({
        csv_content: content,
        import_mode: importMode,
      });
      setPreview(res.data);
      setStep("preview");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async (newMode) => {
    setImportMode(newMode);
    if (csvContent) {
      // Re-preview with new mode
      setLoading(true);
      try {
        const res = await previewStaffImport({
          csv_content: csvContent,
          import_mode: newMode,
        });
        setPreview(res.data);
      } catch (err) {
        toast.error(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExecute = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);
    
    try {
      const res = await executeStaffImport({
        csv_content: csvContent,
        import_mode: importMode,
        confirmed: true,
      });
      setResults(res.data);
      setStep("results");
      
      if (res.data.success) {
        toast.success(
          `Import complete: ${res.data.created_count} created, ${res.data.updated_count} updated`
        );
      } else {
        toast.warning(
          `Import complete with errors: ${res.data.error_count} failed`
        );
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep("upload");
    setCsvContent("");
    setFileName("");
    setPreview(null);
    setResults(null);
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await getStaffImportLogs({ limit: 20 });
      setLogs(res.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLogsLoading(false);
    }
  };

  const toggleLogs = () => {
    if (!showLogs) {
      loadLogs();
    }
    setShowLogs(!showLogs);
  };

  return (
    <div className="space-y-6" data-testid="staff-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Crew CSV Import</h1>
          <p className="text-muted-foreground text-sm">
            Import crew records from CSV with preview and validation
          </p>
        </div>
        <Button variant="outline" onClick={toggleLogs}>
          <History className="h-4 w-4 mr-2" />
          {showLogs ? "Hide" : "Show"} Import History
        </Button>
      </div>

      {/* Import History */}
      {showLogs && (
        <Card className="bg-surface-elevated/50 border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Import History</CardTitle>
          </CardHeader>
          <CardContent>
            <ImportLogsTable logs={logs} loading={logsLoading} />
          </CardContent>
        </Card>
      )}

      {/* Info Alert */}
      <Alert className="border-blue-700 bg-blue-900/20">
        <Info className="h-4 w-4 text-blue-400" />
        <AlertTitle className="text-blue-300">Safe Import Process</AlertTitle>
        <AlertDescription className="text-blue-200/80">
          <ul className="list-disc list-inside text-sm space-y-1 mt-2">
            <li>Upload and preview before any changes are made</li>
            <li>Choose between creating new staff or updating existing</li>
            <li>Empty values will not overwrite existing data</li>
            <li>Email is used as the unique identifier for matching</li>
            <li>All imports are logged for audit purposes</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Main Content */}
      <Card className="bg-surface-elevated/50 border-border">
        <CardHeader>
          <CardTitle>
            {step === "upload" && "Step 1: Upload CSV File"}
            {step === "preview" && "Step 2: Review Changes"}
            {step === "results" && "Import Complete"}
          </CardTitle>
          <CardDescription>
            {step === "upload" && "Select a CSV file to import crew records"}
            {step === "preview" && "Verify the changes before executing the import"}
            {step === "results" && "Review the import results"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Import Mode Selector */}
          {(step === "upload" || step === "preview") && (
            <div className="space-y-2">
              <Label>Import Mode</Label>
              <Select value={importMode} onValueChange={handleModeChange}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(IMPORT_MODES).map(([value, { label }]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {IMPORT_MODES[importMode].description}
              </p>
            </div>
          )}

          {/* Step: Upload */}
          {step === "upload" && (
            <FileDropZone onFileSelect={handleFileSelect} disabled={loading} />
          )}

          {/* Step: Preview */}
          {step === "preview" && preview && (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>{fileName}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="ml-auto"
                >
                  Change File
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className="bg-surface-base border-border">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-white">{preview.total_rows}</p>
                    <p className="text-xs text-muted-foreground">Total Rows</p>
                  </CardContent>
                </Card>
                <Card className="bg-surface-base border-green-700">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{preview.create_count}</p>
                    <p className="text-xs text-muted-foreground">To Create</p>
                  </CardContent>
                </Card>
                <Card className="bg-surface-base border-blue-700">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-400">{preview.update_count}</p>
                    <p className="text-xs text-muted-foreground">To Update</p>
                  </CardContent>
                </Card>
                <Card className="bg-surface-base border-yellow-700">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-400">{preview.skip_count}</p>
                    <p className="text-xs text-muted-foreground">To Skip</p>
                  </CardContent>
                </Card>
                <Card className="bg-surface-base border-red-700">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-red-400">{preview.error_count}</p>
                    <p className="text-xs text-muted-foreground">Errors</p>
                  </CardContent>
                </Card>
              </div>

              {/* Validation Summary */}
              {preview.validation_summary?.length > 0 && (
                <Alert className={preview.blocking_errors ? "border-red-700 bg-red-900/20" : "border-yellow-700 bg-yellow-900/20"}>
                  {preview.blocking_errors ? (
                    <AlertCircle className="h-4 w-4 text-red-400" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-yellow-400" />
                  )}
                  <AlertTitle className={preview.blocking_errors ? "text-red-300" : "text-yellow-300"}>
                    {preview.blocking_errors ? "Blocking Errors" : "Warnings"}
                  </AlertTitle>
                  <AlertDescription className={preview.blocking_errors ? "text-red-200/80" : "text-yellow-200/80"}>
                    <ul className="list-disc list-inside text-sm space-y-1 mt-1">
                      {preview.validation_summary.map((msg, i) => (
                        <li key={i}>{msg}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Preview Table */}
              <div>
                <Label className="mb-2 block">Row Preview</Label>
                <PreviewTable rows={preview.rows} />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={handleReset}>
                  Cancel
                </Button>
                <Button
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={preview.blocking_errors || loading || 
                    (preview.create_count === 0 && preview.update_count === 0)}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="execute-import-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Execute Import
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Results */}
          {step === "results" && results && (
            <div className="space-y-4">
              {/* Results Summary */}
              <Alert className={results.success ? "border-green-700 bg-green-900/20" : "border-yellow-700 bg-yellow-900/20"}>
                {results.success ? (
                  <CheckCircle className="h-4 w-4 text-green-400" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                )}
                <AlertTitle className={results.success ? "text-green-300" : "text-yellow-300"}>
                  {results.success ? "Import Successful" : "Import Completed with Errors"}
                </AlertTitle>
                <AlertDescription className="text-foreground">
                  <div className="flex gap-6 mt-2">
                    <span className="text-green-400">{results.created_count} created</span>
                    <span className="text-blue-400">{results.updated_count} updated</span>
                    <span className="text-yellow-400">{results.skipped_count} skipped</span>
                    <span className="text-red-400">{results.error_count} errors</span>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Errors List */}
              {results.errors?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-red-400">Errors:</Label>
                  <div className="bg-red-900/20 border border-red-700 rounded-lg p-3 max-h-[200px] overflow-auto">
                    <ul className="list-disc list-inside text-sm text-red-300 space-y-1">
                      {results.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button onClick={handleReset}>
                  Import Another File
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Import</DialogTitle>
            <DialogDescription>
              Are you sure you want to execute this import?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-foreground mb-4">
              This action will:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              {importMode === "create_new" ? (
                <li>Create {preview?.create_count || 0} new crew records</li>
              ) : (
                <li>Update {preview?.update_count || 0} existing crew records</li>
              )}
              <li>Skip {preview?.skip_count || 0} rows</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              This action will be logged for audit purposes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExecute} className="bg-teal-600 hover:bg-teal-700">
              Confirm Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
